import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  JsonApiResponseDto,
  Response,
  TQueryParams,
  formatQueryParams,
  parseJsonApiResponse,
  prepareHeaders,
} from 'evolve-ui';

export enum PortalDocumentType {
  GRAPHIC_IMAGE = 'GRAPHIC_IMAGE',
  INSTITUTIONAL_DOCUMENTS = 'INSTITUTIONAL_DOCUMENTS',
  EVOLVE_GRH = 'EVOLVE_GRH',
  EVOLVE_ETT = 'EVOLVE_ETT',
  EVOLVE = 'EVOLVE',
  SGQ_PR01 = 'SGQ_PR01',
  SGQ_PR02 = 'SGQ_PR02',
  SGQ_PR03 = 'SGQ_PR03',
  SGQ_PR04 = 'SGQ_PR04',
  SGQ_PR05 = 'SGQ_PR05',
  SGQ_PR06 = 'SGQ_PR06',
  SGQ_PR07 = 'SGQ_PR07',
  RGPD = 'RGPD',
}

export type TPortalDocument = {
  id: string;
  name: string;
  link: string;
  docType?: PortalDocumentType;
  uploadedAt?: string;
  uploadedBy?: string;
  uploadedById?: string;
};

export type TPortalDocumentBlob = {
  objectUrl: string;
  contentType: string;
  filename: string;
};

function parseFilenameFromContentDisposition(disposition: string): string {
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;\s]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }
  const asciiMatch = disposition.match(/filename="([^"]+)"/i);
  if (asciiMatch?.[1]) return asciiMatch[1];
  const unquotedMatch = disposition.match(/filename=([^;\s]+)/i);
  if (unquotedMatch?.[1]) return unquotedMatch[1];
  return 'document';
}

export const portalDocumentsApi = createApi({
  reducerPath: 'portalDocuments',
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.GATSBY_PORTAL_DOCUMENTS_API_URL}`,
    prepareHeaders,
  }),
  tagTypes: ['PortalDocuments'],
  endpoints: (builder) => ({
    getPortalDocuments: builder.query<
      Response<TPortalDocument>,
      TQueryParams & { documentType: PortalDocumentType }
    >({
      providesTags: ['PortalDocuments'],
      query: ({ documentType, ...params }) =>
        formatQueryParams({
          ...params,
          filter: [
            ...(params.filter ?? []),
            { name: 'type', value: documentType },
          ],
        }),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TPortalDocument>(
          res as JsonApiResponseDto<TPortalDocument>,
          meta,
          args,
        ),
    }),
    getPortalDocumentBlob: builder.query<TPortalDocumentBlob, { docId: string }>({
      queryFn: async ({ docId }, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `${docId}`,
          responseHandler: async (response) => {
            const contentType = response.headers.get('content-type') ?? '';
            const disposition = response.headers.get('content-disposition') ?? '';
            if (!response.ok) {
              return { ok: false as const, status: response.status };
            }
            const blob = await response.blob();
            return { ok: true as const, blob, contentType, disposition };
          },
        });

        if (result.error) {
          return { error: result.error };
        }

        const payload = result.data as
          | { ok: true; blob: Blob; contentType: string; disposition: string }
          | { ok: false; status: number };

        if (!payload || !payload.ok) {
          return {
            error: {
              status: payload && 'status' in payload ? payload.status : 404,
              data: 'Documento não encontrado',
            },
          };
        }

        const urlApi = window.URL || window.webkitURL;
        const objectUrl = urlApi.createObjectURL(payload.blob);

        return {
          data: {
            objectUrl,
            contentType: payload.contentType,
            filename: parseFilenameFromContentDisposition(payload.disposition),
          },
        };
      },
    }),
    getPortalDocument: builder.query<TPortalDocument, { docId: string }>({
      query: ({ docId }) => ({ url: `metadata/${docId}` }),
      transformResponse: (res, meta, args) => {
        const parsed = parseJsonApiResponse<TPortalDocument>(
          res as JsonApiResponseDto<TPortalDocument>,
          meta,
          args,
        );
        return parsed.values[0];
      },
    }),
    openPortalDocument: builder.mutation({
      queryFn: async ({ docId }, _api, _extraOptions, baseQuery) => {
        const result: any = await baseQuery({
          url: `${docId}`,
          responseHandler: (response) => response.blob(),
        });
        try {
          const hiddenElement = document.createElement('a');
          const url = window.URL || window.webkitURL;
          if (!result.data) {
            return { error: 'Documento não encontrado' };
          }
          const blobUrl = url.createObjectURL(result.data);
          hiddenElement.href = blobUrl;
          hiddenElement.target = '_blank';
          hiddenElement.click();
        } catch (err: any) {
          return { error: err?.message };
        }
        return { data: null };
      },
    }),
    downloadPortalDocument: builder.mutation({
      queryFn: async ({ docId, filename }, _api, _extraOptions, baseQuery) => {
        const result: any = await baseQuery({
          url: `${docId}`,
          responseHandler: (response) => response.blob(),
        });
        try {
          const hiddenElement = document.createElement('a');
          const url = window.URL || window.webkitURL;
          if (!result.data) {
            return { error: 'Documento não encontrado' };
          }
          const blobUrl = url.createObjectURL(result.data);
          hiddenElement.href = blobUrl;
          hiddenElement.download = filename;
          hiddenElement.click();
        } catch (err: any) {
          return { error: err?.message };
        }
        return { data: null };
      },
    }),
    uploadPortalDocument: builder.mutation<
      TPortalDocument,
      { type: PortalDocumentType; file: File; originalName: string }
    >({
      invalidatesTags: ['PortalDocuments'],
      query: ({ type, file, originalName }) => {
        const body = new FormData();
        body.append('document', file);
        body.append('type', type);
        body.append('originalName', originalName);
        return {
          url: '',
          method: 'POST',
          body,
        };
      },
    }),
    deletePortalDocument: builder.mutation({
      invalidatesTags: ['PortalDocuments'],
      query: ({ docId }) => ({
        url: `${docId}`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useGetPortalDocumentsQuery,
  useGetPortalDocumentBlobQuery,
  useGetPortalDocumentQuery,
  useOpenPortalDocumentMutation,
  useDownloadPortalDocumentMutation,
  useUploadPortalDocumentMutation,
  useDeletePortalDocumentMutation,
} = portalDocumentsApi;
